import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import BottomTab from "../../../components/pagination/BottomTab";
import { CardSkeleton, TableSkeleton } from "../../../components/skeleton/ListSkeleton";
import { AtRiskLevel, AtRiskListData, SourceKey, getPatients, getPatientsAtRisk } from "../analyticsApi";
import { bucketLabel, cohortReturnPct, formatNumber, formatPct, formatRs, longDate, seriesIsEmpty, sourceLabel } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import { CategoryBarChart, PeriodBarChart } from "../components/charts";
import { ChartCard, EmptyRow, TD, TD_NUM, TH, TH_NUM, TR, TableCard, chartSx, seriesColors, useIsDark } from "../components/ui";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";

const SOURCES: SourceKey[] = ["pre_booked", "walk_in", "online", "other"];
const PAGE_SIZE = 20;

function ActiveBaseCharts({ points }: { points: { month: string; activeBase: number | null; gained: number | null; lost: number | null }[] }) {
  const isDark = useIsDark();
  const colors = seriesColors(isDark);
  const labels = points.map((p) => bucketLabel(p.month));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard title="Active patient base" hint="Patients with a visit or payment in the trailing 12 months, per month-end">
        <LineChart
          height={260}
          xAxis={[{ scaleType: "point", data: labels }]}
          series={[{ id: "activeBase", label: "Active patients", data: points.map((p) => p.activeBase), color: colors[0], valueFormatter: (v: number | null) => formatNumber(v), area: true }]}
          grid={{ horizontal: true }}
          margin={{ left: 56, right: 12, top: 36, bottom: 32 }}
          slotProps={{ legend: { hidden: true } }}
          sx={chartSx(isDark)}
        />
      </ChartCard>
      <ChartCard title="Gained vs lost" hint="Change in the active base against the previous month-end">
        <BarChart
          height={260}
          xAxis={[{ scaleType: "band", data: labels }]}
          series={[
            { id: "gained", label: "Gained", data: points.map((p) => p.gained ?? 0), color: colors[2], valueFormatter: (v: number | null) => formatNumber(v) },
            { id: "lost", label: "Lost", data: points.map((p) => p.lost ?? 0), color: colors[1], valueFormatter: (v: number | null) => formatNumber(v) },
          ]}
          borderRadius={4}
          grid={{ horizontal: true }}
          margin={{ left: 56, right: 12, top: 36, bottom: 32 }}
          sx={chartSx(isDark)}
        />
      </ChartCard>
    </div>
  );
}

export default function AnalyticsPatients() {
  const period = useAnalyticsPeriod();
  const { data, loading } = useAnalyticsFetch(() => getPatients({ from: period.from, to: period.to }), [period.from, period.to]);

  const [level, setLevel] = useState<AtRiskLevel>("amber");
  const [page, setPage] = useState(1);
  const [atRisk, setAtRisk] = useState<AtRiskListData | null>(null);
  const [atRiskLoading, setAtRiskLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setAtRiskLoading(true);
    getPatientsAtRisk(level, page, PAGE_SIZE)
      .then((d) => {
        if (!cancelled) setAtRisk(d);
      })
      .catch((err) => {
        console.error(err?.response || err);
        if (!cancelled) toast.error("Could not load at-risk patients");
      })
      .finally(() => {
        if (!cancelled) setAtRiskLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [level, page]);

  const changeLevel = (next: AtRiskLevel) => {
    setLevel(next);
    setPage(1);
  };

  const noData = !loading && data != null && seriesIsEmpty(data.series, ["new"]) && data.activeBaseSeries.every((p) => !p.activeBase);

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CardSkeleton cards={4} height="h-28" />
        </div>
      ) : noData ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <KpiCard
              label="New patients"
              value={formatNumber(data.newPatients.current)}
              change={data.newPatients.change}
              sparkline={data.series.map((p) => (typeof p.new === "number" ? p.new : null))}
            />
            <div className="lg:col-span-2">
              <ChartCard title="New patients" hint={data.period.granularity === "month" ? "Per month" : "Per day"}>
                <PeriodBarChart series={data.series} specs={[{ key: "new", label: "New patients" }]} height={200} />
              </ChartCard>
            </div>
          </div>

          <ActiveBaseCharts points={data.activeBaseSeries} />

          <div>
            <h3 className="text-sm font-semibold mb-3">Appointments by source</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {SOURCES.map((s) => (
                <KpiCard key={s} label={sourceLabel(s)} value={formatNumber(data.sources[s]?.current ?? null)} change={data.sources[s]?.change ?? null} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TableCard title="Registration cohorts" hint={`Share of each month's new patients who came back · as of ${longDate(data.asOfDay)}`}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-600">
                    <th className={TH}>Month</th>
                    <th className={TH_NUM}>Registered</th>
                    <th className={TH_NUM}>Back in 3 mo</th>
                    <th className={TH_NUM}>6 mo</th>
                    <th className={TH_NUM}>12 mo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                  {data.cohorts.length === 0 ? (
                    <EmptyRow colSpan={5}>No cohorts yet</EmptyRow>
                  ) : (
                    data.cohorts.map((c) => (
                      <tr key={c.month} className={TR}>
                        <td className={TD}>{bucketLabel(c.month)}</td>
                        <td className={TD_NUM}>{formatNumber(c.size)}</td>
                        <td className={TD_NUM}>{formatPct(cohortReturnPct(c.returned3, c.size))}</td>
                        <td className={TD_NUM}>{formatPct(cohortReturnPct(c.returned6, c.size))}</td>
                        <td className={TD_NUM}>{formatPct(cohortReturnPct(c.returned12, c.size))}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>

            <div className="space-y-4">
              <ChartCard title="Patients by gender" hint="Active, non-provisional patients">
                <CategoryBarChart
                  labels={data.demographics.gender.map((g) => g.key.charAt(0).toUpperCase() + g.key.slice(1))}
                  values={data.demographics.gender.map((g) => g.count)}
                  label="Patients"
                  height={160}
                  horizontal
                />
              </ChartCard>
              <ChartCard title="Patients by age">
                <CategoryBarChart labels={data.demographics.age.map((a) => a.key)} values={data.demographics.age.map((a) => a.count)} label="Patients" height={200} />
              </ChartCard>
            </div>
          </div>

          <TableCard title="Top 20 patients" hint="By amount collected in this period">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className={TH}>#</th>
                  <th className={TH}>Patient</th>
                  <th className={TH}>Reg no</th>
                  <th className={TH_NUM}>Receipts</th>
                  <th className={TH_NUM}>Collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {data.topPatients.length === 0 ? (
                  <EmptyRow colSpan={5} />
                ) : (
                  data.topPatients.map((p, i) => (
                    <tr key={p.patientId} className={TR}>
                      <td className={TD}>{i + 1}</td>
                      <td className={TD}>{p.fullName || "—"}</td>
                      <td className={TD}>{p.regNo || "—"}</td>
                      <td className={TD_NUM}>{formatNumber(p.receipts)}</td>
                      <td className={TD_NUM}>{formatRs(p.collected)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      <TableCard
        title="At-risk patients"
        hint={
          data?.atRisk
            ? `${formatNumber(data.atRisk.amber)} idle 6–12 months · ${formatNumber(data.atRisk.red)} idle over 12 months (snapshot) · list is live`
            : "Patients with no visit or payment for 6 months or more (live)"
        }
        action={
          <div className={`flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-700`}>
            {(["amber", "red"] as AtRiskLevel[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => changeLevel(l)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  level === l
                    ? l === "amber"
                      ? "bg-white text-amber-600 shadow-sm dark:bg-slate-600 dark:text-amber-300"
                      : "bg-white text-rose-600 shadow-sm dark:bg-slate-600 dark:text-rose-300"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                {l === "amber" ? "6–12 months" : "Over 12 months"}
              </button>
            ))}
          </div>
        }
        footer={<BottomTab key={level} title="patients" count={atRisk?.level === level ? atRisk.count : 0} onPageChange={setPage} pageSize={PAGE_SIZE} />}
      >
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-600">
              <th className={TH}>Patient</th>
              <th className={TH}>Reg no</th>
              <th className={TH}>Mobile</th>
              <th className={TH}>Last activity</th>
              <th className={TH_NUM}>Months idle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
            {atRiskLoading || !atRisk ? (
              <TableSkeleton rows={5} colSpan={5} />
            ) : atRisk.list.length === 0 ? (
              <EmptyRow colSpan={5}>No patients in this group</EmptyRow>
            ) : (
              atRisk.list.map((p) => (
                <tr key={p._id} className={TR}>
                  <td className={TD}>{p.fullName || "—"}</td>
                  <td className={TD}>{p.regNo || "—"}</td>
                  <td className={TD}>{p.mobileNo || p.whatsapp || "—"}</td>
                  <td className={TD}>{p.last_activity_at ? longDate(String(p.last_activity_at).slice(0, 10)) : "—"}</td>
                  <td className={TD_NUM}>{formatNumber(p.monthsIdle)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>
    </div>
  );
}
